import React, { useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ModuleGlyph } from './glyphs';
import { useAlertsCount, usePlannedCount, useReviewCount, useRefreshHealth } from './shellBadges';
import RefreshHealthBadge from './RefreshHealthBadge';
import { useBenchmarkIssueCount } from './benchmarkIssues';   // fb-108
import LegacyPill, { LEGACY_SECTIONS } from './LegacyPill';
import { getAttentionCached, peekAttention } from './attnCache';   // fb-71: Payments overdue badge (shared cache)
import useNavLabels from './useNavLabels';
import { useDispatchCount, badgeText } from './dispatchCount';
import { useInvoiceCount } from './invoiceCount';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from 'react-i18next';

const landing = module => module.sections.find(section => section.isLanding) || module.sections[0];
const destination = module => `/${module.key}/${landing(module).key}`;

export default function ModuleBar({ modules, activeModule }) {
  const { t } = useTranslation('common');
  const nav = useNavLabels();
  const benchIssues = useBenchmarkIssueCount();   // fb-108
  // Round 1 (C1): every badge here sits INSIDE a dropdown, so none is fetched at boot: the shared stores (also read by LeftMenu)
  // start on the first dropdown open. Before, this bar fired 4 alert feeds + the whole planned-transactions payload on every page.
  const [opened, setOpened] = useState(false);
  const dispatchN = useDispatchCount(opened);   // open + draft slips (Sales > Dispatches dropdown item)
  const { isVisible, isAdmin } = useAuth(); const invoicesVisible = isVisible('sales-invoices');   // role without the Invoices page: no nav item, no badge fetch (no 403)
  const invoiceN = useInvoiceCount(opened && modules.some(m => m.sections.some(s => s.key === 'invoices')) && invoicesVisible);   // To-invoice tab population (Sales > Invoices dropdown item)
  const reviewCount = useReviewCount(opened && !!isAdmin);   // Approvals is an admin page: no count fetch (and no console 403) for anyone else
  const alertsCount = useAlertsCount(opened);
  const plannedCount = usePlannedCount(opened);
  const refreshHealth = useRefreshHealth(opened && !!isAdmin);   // 47284
  const [attn, setAttn] = useState(peekAttention());   // fb-71

  const [drop, setDrop] = useState(null);   // { module, top, left } — anchored under the hovered tab
  const location = useLocation();
  const activeSection = location.pathname.split('/')[2] || '';
  const closeTimer = useRef(null);

  const openFor = (module, event) => {
    clearTimeout(closeTimer.current);
    if (module.sections.length <= 1) { setDrop(null); return; }
    setOpened(true);
    const r = event.currentTarget.getBoundingClientRect();
    setDrop({ module, top: r.bottom + 2, left: Math.min(r.left, window.innerWidth - 250) });
    // fb-71 — only fire the heavy attention query when opening a dropdown that has a Payments/Payables item.
    if (module.sections.some(s => s.key === 'payments' || s.key === 'payables')) getAttentionCached().then(setAttn).catch(() => {});
  };
  const scheduleClose = () => { clearTimeout(closeTimer.current); closeTimer.current = setTimeout(() => setDrop(null), 90); };
  const keepOpen = () => clearTimeout(closeTimer.current);

  return <>
    <nav className="mbar" aria-label={t('chromeUi.modules')} onMouseLeave={scheduleClose}>
      {modules.map(module => <Link key={module.key} className={`mtab${module.key === activeModule ? ' active' : ''}`} to={destination(module)}
        onMouseEnter={event => openFor(module, event)} onFocus={event => openFor(module, event)}
        aria-current={module.key === activeModule ? 'page' : undefined}>
        <ModuleGlyph module={module} /><span>{nav.mod(module)}</span>{module.sections.length > 1 && <span className="caret">▾</span>}
      </Link>)}
    </nav>
    {drop && <nav className="mdrop open" style={{ top: drop.top, left: drop.left, right: 'auto' }}
      aria-label={`${nav.mod(drop.module)} sections`} onMouseEnter={keepOpen} onMouseLeave={() => setDrop(null)}>
      <div className="md-lab"><span className="eyebrow"><ModuleGlyph module={drop.module} />{nav.mod(drop.module)}</span></div>
      {drop.module.sections.filter(section => !section.hidden).filter(section => section.key !== 'invoices' || invoicesVisible).slice().sort((a, b) => (a.menuOrder ?? 1e6) - (b.menuOrder ?? 1e6)).map(section => <React.Fragment key={section.key}><Link
        className={`mdrop-link${drop.module.key === activeModule && section.key === activeSection ? ' cur' : ''}`}
        to={`/${drop.module.key}/${section.key}`} onClick={() => setDrop(null)}>
        <span className="mic">{section.icon || '›'}</span>{nav.sec(drop.module, section)}{LEGACY_SECTIONS.has(`${drop.module.key}/${section.key}`) && <LegacyPill cls="mbadge" />}{section.key === 'approvals' && reviewCount != null
          ? (reviewCount > 0 && <span className="mbadge">{reviewCount}</span>)
          : section.key === 'dispatches' && dispatchN != null
          ? (dispatchN > 0 && <span className="mbadge" title={nav.aria('dispatches', dispatchN)} aria-label={nav.aria('dispatches', dispatchN)}>{badgeText(dispatchN)}</span>)
          : section.key === 'invoices' && invoiceN != null
          ? (invoiceN > 0 && <span className="mbadge" title={nav.aria('invoices', invoiceN)} aria-label={nav.aria('invoices', invoiceN)}>{badgeText(invoiceN)}</span>)
          : section.key === 'alerts' && alertsCount != null
          ? (alertsCount > 0 && <span className="mbadge" style={{ background: '#fef2f2', color: '#dc2626', border: '1px solid #fecaca' }}>{alertsCount}</span>)
          : section.key === 'review-post' && plannedCount != null
          ? (plannedCount > 0 && <span className="mbadge">{plannedCount}</span>)
          : section.key === 'benchmarks'
          ? (benchIssues > 0 && <span className="mbadge" style={{ background: '#fef2f2', color: '#dc2626', border: '1px solid #fecaca' }}>{benchIssues}</span>)
          : section.key === 'data-refreshes'
          ? <RefreshHealthBadge health={refreshHealth} cls="mbadge" />
          : (section.badge && <span className="mbadge">{section.badge}</span>)}{(section.key === 'payments' || section.key === 'payables') && (attn?.payable?.overdue_orders || 0) > 0 && <span className="mbadge" title={t('chromeUi.overduePay', { count: attn.payable.overdue_orders })} style={{ background: '#fef2f2', color: '#dc2626', border: '1px solid #fecaca' }}>{attn.payable.overdue_orders}</span>}
      </Link>{section.groupEnd && <div className="m-sep" />}</React.Fragment>)}
    </nav>}
  </>;
}
